import { useMemo, useState, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Typography, TextButton, Card, styled, IconButton, useMediaQuery } from 'bonfire-kindling';
import { StackedList, ConfirmationModal } from 'components';
import { useDeleteSearchProfile } from 'helpers/opportunities';

const ResponsiveListItem = ({ children }) => {
  const isTabletOrDesktop = useMediaQuery(theme => theme.breakpoints.up('sm'));

  return isTabletOrDesktop ? (
    <Card
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        p: 4,
      }}
    >
      {children}
    </Card>
  ) : (
    <StackedList.Item>{children}</StackedList.Item>
  );
};

const StyledTextButton = styled(TextButton)(() => ({
  fontWeight: 'bold',
  textDecoration: 'underline',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const SavedSearchSettingsListItem = ({ id, name }) => {
  const { t } = useTranslation();
  const isTabletOrDesktop = useMediaQuery(theme => theme.breakpoints.up('sm'));
  const elementId = useMemo(() => `${id}-settings-saved-search`, [id]);
  const [modalOpen, setModalOpen] = useState(false);
  const closeModal = useCallback(() => setModalOpen(false), []);

  const {
    mutation: { mutate },
  } = useDeleteSearchProfile();

  const deleteSavedSearch = useCallback(() => {
    mutate(id);
    setModalOpen(false);
  }, [mutate, id]);

  return (
    <ResponsiveListItem>
      <Typography
        id={elementId}
        component="p"
        variant="body2"
        sx={{
          flex: '1 1 0%',
          minWidth: 0,
          mr: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontWeight: isTabletOrDesktop ? 'normal' : 'bold',
        }}
      >
        {name}
      </Typography>
      {isTabletOrDesktop ? (
        <>
          <StyledTextButton
            component={RouterLink}
            label={t('settings.saved_search.overview.edit_saved_search')}
            variant="text"
            to={`/settings/saved/${id}/edit`}
            aria-describedby={elementId}
          />
          <StyledTextButton
            label={t('settings.saved_search.overview.remove_saved_search')}
            variant="text"
            onClick={() => setModalOpen(true)}
            aria-describedby={elementId}
          />
        </>
      ) : (
        <>
          <IconButton
            component={RouterLink}
            iconName="pencil-alt"
            aria-label={t('settings.saved_search.overview.edit_saved_search')}
            aria-describedby={elementId}
            size="small"
            to={`/settings/saved/${id}/edit`}
            borderless
          />
          <IconButton
            iconName="trash"
            onClick={() => setModalOpen(true)}
            aria-label={t('settings.saved_search.overview.remove_saved_search')}
            aria-describedby={elementId}
            size="small"
            borderless
          />
        </>
      )}
      <ConfirmationModal
        open={!!modalOpen}
        title={t('settings.saved_search.modal.confirm_remove')}
        onClose={closeModal}
        CancelButtonProps={{ label: t('common.cancel'), onClick: closeModal }}
        ConfirmButtonProps={{ label: t('common.remove'), onClick: deleteSavedSearch }}
      />
    </ResponsiveListItem>
  );
};

export default SavedSearchSettingsListItem;
